import { Fields as MerchantFields, OrderingProvider } from 'models/Merchant'
import { promoCodeCashierWalkthroughUrls } from 'utilities/urlUtils'
import { COMING_SOON_PROMO_POS, hasComingSoonPromoPos } from './posSupport'

// Status-pill tints shared by the two promo-code compatibility surfaces — the
// Overview `CompatibilityCard` and the LOY-622 `ChannelStatusCell`. Figma Inspect
// values with no equivalent in `constants/palette.ts`; they live here (imported
// by both) rather than being duplicated per file or bloating the shared palette.
export const LIVE_GREEN = '#1D8348'
export const NEEDS_ACTIVATION_GOLD = '#AD8714'
// Accessible variant of the gold for TEXT. `#AD8714` is 3.36:1 on white, below
// WCAG AA's 4.5:1 for normal-size text; `#8C6D0E` clears 4.5:1 on white, grey-05,
// and the needs-activation tint. The dot (a graphical object, 3:1 bar) keeps the
// original brand gold; only text uses this darker value.
export const NEEDS_ACTIVATION_GOLD_TEXT = '#8C6D0E'

export type CompatibilityState =
  | 'live'
  | 'coming_soon'
  | 'needs_activation'
  | 'not_supported'

export type CompatibilityRow = {
  key: string
  label: string
  logoKey: string | null
  state: CompatibilityState
  walkthroughUrl?: string
}

// `supported` tracks whether the provider has a promo-code path in
// thanx-ordering (Olo coupon API, Toast::ApplyPromoCode, Deliverect basket
// discount). Square and Qu ordering have none, so they read as not supported.
const ORDERING_PROVIDERS: Record<
  OrderingProvider,
  { label: string; logoKey: string; supported: boolean }
> = {
  olo: { label: 'Olo', logoKey: 'olo', supported: true },
  toast: { label: 'Toast', logoKey: 'toast', supported: true },
  deliverect: { label: 'Deliverect', logoKey: 'deliverect', supported: true },
  square: { label: 'Square', logoKey: 'square', supported: false },
  qu: { label: 'Qu', logoKey: 'qu', supported: false },
}

export const OLO_ORDERING_PROVIDER: OrderingProvider = 'olo'

// `Pos::Provider#value`s with an in-store promo code redemption path in the
// offer service (`<provider>/loyalty/promo_code`). There is no backend constant
// that classifies this, so the list must be extended by hand when a POS gains
// in-store promo code support.
const INSTORE_PROMO_POS: Record<string, { walkthroughUrl: string }> = {
  ncr: { walkthroughUrl: promoCodeCashierWalkthroughUrls.ncr },
  brink_direct: { walkthroughUrl: promoCodeCashierWalkthroughUrls.brink },
  genius: { walkthroughUrl: promoCodeCashierWalkthroughUrls.genius },
}

// Toast in-store redemption rolls out behind `toast-promo-instore-beta`, so it
// is resolved at call time (see posInstoreState) rather than listed above.
const TOAST_POS = 'toast'

const POS_LOGO_KEYS: Record<string, string> = {
  brink_direct: 'par_brink',
  franpos: 'franpos',
  genius: 'genius_xenial',
  ncr: 'ncr',
  qu: 'qu',
  revel: 'revel',
  shift4: 'shift4',
  square: 'square',
  square_v2: 'square',
  tabit: 'tabit',
  toast: 'toast',
}

// Strict false check: `olo_promotions_enabled` is absent from the merchant
// payload on older API deploys, and an undefined value must not be read as
// "needs activation" for merchants whose Olo promo codes already work.
const orderingState = (
  provider: OrderingProvider,
  supported: boolean,
  merchant: MerchantFields | null | undefined
): CompatibilityState => {
  if (!supported) return 'not_supported'
  if (
    provider === OLO_ORDERING_PROVIDER &&
    merchant?.olo_promotions_enabled === false
  ) {
    return 'needs_activation'
  }
  return 'live'
}

export const orderingCompatibility = (
  merchant: MerchantFields | null | undefined
): CompatibilityRow[] => {
  const provider = merchant?.ordering_provider
  if (!provider) return []

  const config = ORDERING_PROVIDERS[provider]
  if (!config) return []

  return [
    {
      key: provider,
      label: config.label,
      logoKey: config.logoKey,
      state: orderingState(provider, config.supported, merchant),
    },
  ]
}

// Supported POS's surface at the top of the in-store list; everything else
// follows in its original API order.
const STATE_SORT_RANK: Record<CompatibilityState, number> = {
  live: 0,
  needs_activation: 1,
  coming_soon: 2,
  not_supported: 3,
}

// Toast in-store promo redemption rolls out per-merchant behind the
// `toast-promo-instore-beta` flag (`toastInstoreEnabled`): beta merchants read
// "supported", everyone else on the coming-soon roadmap (Toast today, via
// COMING_SOON_PROMO_POS — the same source of truth as PosRedemptionBanner)
// reads "coming soon". The flag check runs BEFORE the coming-soon check so it
// wins for beta merchants; rolling the flag to 100% at GA flips everyone to
// "supported" with no code change.
const posInstoreState = (
  providerValue: string,
  hasInstorePath: boolean,
  toastInstoreEnabled: boolean
): CompatibilityState => {
  if (hasInstorePath) return 'live'
  if (providerValue === TOAST_POS && toastInstoreEnabled) return 'live'
  if (COMING_SOON_PROMO_POS.includes(providerValue)) return 'coming_soon'
  return 'not_supported'
}

export const posCompatibility = (
  merchant: MerchantFields | null | undefined,
  toastInstoreEnabled: boolean
): CompatibilityRow[] =>
  (merchant?.all_redemption_providers ?? [])
    .map((provider): CompatibilityRow => {
      const instore = INSTORE_PROMO_POS[provider.value]

      return {
        key: provider.value,
        label: provider.label,
        logoKey: POS_LOGO_KEYS[provider.value] ?? null,
        state: posInstoreState(
          provider.value,
          Boolean(instore),
          toastInstoreEnabled
        ),
        walkthroughUrl: instore?.walkthroughUrl,
      }
    })
    // Array.prototype.sort is stable, so providers keep their original API
    // order within each support group — only the groups are reordered.
    .sort((a, b) => STATE_SORT_RANK[a.state] - STATE_SORT_RANK[b.state])

// Per-promotion channel acceptance (LOY-622). Layers the reward template's
// channel restriction (`redemption_venue`) on top of the merchant's provider
// compatibility. Precedence matches the ticket: provider availability first,
// then the template restriction.
export type ChannelAcceptance =
  | 'enabled'
  | 'needs_activation'
  | 'disabled_in_template'
  | 'pos_coming_soon'
  | 'pos_not_supported'
  | 'provider_unavailable'

// `orderingCompatibility` yields 0-or-1 row (a merchant has a single
// `ordering_provider`), so the online channel keys off that one row.
export const onlineAcceptance = (
  merchant: MerchantFields | null | undefined,
  redemptionVenue: string
): ChannelAcceptance => {
  const row = orderingCompatibility(merchant)[0]
  if (!row || row.state === 'not_supported') return 'provider_unavailable'
  // Template restriction wins over activation state: an in-store-only promo can
  // never redeem online even once Olo is activated, so surface
  // 'disabled_in_template' rather than a dead-end 'Needs activation' + Activate
  // CTA that would resolve to 'disabled_in_template' anyway.
  if (redemptionVenue === 'instore') return 'disabled_in_template'
  if (row.state === 'needs_activation') return 'needs_activation'
  return 'enabled'
}

// `posCompatibility` yields one row per redemption provider; the in-store
// channel is available if any provider has a live in-store promo path.
export const posAcceptance = (
  merchant: MerchantFields | null | undefined,
  toastInstoreEnabled: boolean,
  redemptionVenue: string
): ChannelAcceptance => {
  // Toast in-store promo redemption rolls out per-merchant behind
  // `toast-promo-instore-beta`. For a beta merchant (flag on) `posCompatibility`
  // renders Toast as `live`, so the `hasLiveNow` check below returns Enabled.
  // For everyone else Toast renders `coming_soon` (never `live`), so a merchant
  // whose only in-store path is coming-soon Toast reads `pos_coming_soon`.
  // Rolling the flag to 100% at GA flips everyone to Enabled with no code change.
  const comingSoon = hasComingSoonPromoPos(merchant)
  // `.some(live)` = "redeemable in-store at at least one location", not "at every
  // location" — a merchant with a mix of supported and unsupported POS reads
  // Enabled here while still seeing the unsupported banner above the table.
  const hasLiveNow = posCompatibility(merchant, toastInstoreEnabled).some(
    row => row.state === 'live'
  )
  if (hasLiveNow) {
    return redemptionVenue === 'online' ? 'disabled_in_template' : 'enabled'
  }
  if (comingSoon) return 'pos_coming_soon'
  // No live in-store promo path. Distinguish a merchant that IS integrated with
  // a POS that simply has no promo-code path (qu, square, fishbowl, franpos,
  // shift4) from one with no Thanx POS integration at all — the former should
  // not be told to "configure POS". `integration_type` is already on the
  // payload (RedemptionProvider) and read this way elsewhere (RedeemManager).
  const isIntegrated = (merchant?.all_redemption_providers ?? []).some(
    provider => ['direct', 'indirect'].includes(provider.integration_type)
  )
  return isIntegrated ? 'pos_not_supported' : 'provider_unavailable'
}
